import React, { useState } from 'react';
import { ClientPortalLayout } from './ClientPortalLayout';
import { GripVerticalIcon, PlusIcon, SparklesIcon, ClockIcon, UsersIcon, CoffeeIcon } from 'lucide-react';
export function AgendaBuilderPage() {
  const [activeDay, setActiveDay] = useState(1);
  const sessions = [{
    time: '8:00 AM',
    duration: '60m',
    title: 'Welcome Breakfast & Networking',
    type: 'social',
    icon: CoffeeIcon
  }, {
    time: '9:00 AM',
    duration: '90m',
    title: 'Opening Keynote: The Future of Biotech',
    type: 'content',
    icon: UsersIcon
  }, {
    time: '10:30 AM',
    duration: '120m',
    title: 'Strategic Alignment Workshop',
    type: 'workshop',
    icon: SparklesIcon
  }, {
    time: '12:30 PM',
    duration: '60m',
    title: 'Lunch Break',
    type: 'social',
    icon: CoffeeIcon
  }, {
    time: '1:30 PM',
    duration: '90m',
    title: 'Cross-functional Empathy Session',
    type: 'workshop',
    icon: UsersIcon
  }];
  const library = [{
    title: 'Fireside Chat',
    duration: '45m',
    type: 'content'
  }, {
    title: 'RACI Framework Exercise',
    duration: '90m',
    type: 'workshop'
  }, {
    title: 'Team Building Activity',
    duration: '120m',
    type: 'social'
  }, {
    title: 'Executive Readout',
    duration: '60m',
    type: 'content'
  }];
  return <ClientPortalLayout data-id="element-248">
      <div className="p-6 lg:p-8 h-[calc(100vh-4rem)] flex flex-col" data-id="element-249">
        <div className="flex justify-between items-end mb-6 shrink-0" data-id="element-250">
          <div data-id="element-251">
            <h1 className="font-serif-display text-3xl text-slate-900 mb-2" data-id="element-252">
              Agenda Builder
            </h1>
            <p className="text-sm text-slate-500" data-id="element-253">
              Draft and refine your offsite schedule.
            </p>
          </div>
          <button className="px-4 py-2 bg-[#0B1B3B] text-white text-sm font-medium rounded-lg hover:bg-[#0B1B3B]/90 transition-colors" data-id="element-254">
            Publish Agenda
          </button>
        </div>

        <div className="flex gap-6 flex-1 min-h-0" data-id="element-255">
          {/* Main Agenda Area */}
          <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden" data-id="element-256">
            {/* Tabs */}
            <div className="flex border-b border-slate-200 px-2 shrink-0" data-id="element-257">
              {[1, 2, 3].map(day => <button key={day} onClick={() => setActiveDay(day)} className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeDay === day ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-258">
                  Day {day}
                </button>)}
            </div>

            {/* Sessions List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-3" data-id="element-259">
              {sessions.map((session, i) => <div key={i} className="flex items-stretch gap-4 group cursor-grab active:cursor-grabbing" data-id="element-260">
                  <div className="w-20 text-right shrink-0 pt-4" data-id="element-261">
                    <div className="text-sm font-bold text-slate-900" data-id="element-262">
                      {session.time}
                    </div>
                    <div className="text-xs text-slate-400" data-id="element-263">
                      {session.duration}
                    </div>
                  </div>

                  <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center gap-4 hover:border-slate-300 transition-colors" data-id="element-264">
                    <GripVerticalIcon className="w-5 h-5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-265" />
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${session.type === 'social' ? 'bg-orange-100 text-orange-600' : session.type === 'workshop' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`} data-id="element-266">
                      <session.icon className="w-5 h-5" data-id="element-267" />
                    </div>
                    <div className="flex-1" data-id="element-268">
                      <h3 className="font-bold text-slate-900 text-sm" data-id="element-269">
                        {session.title}
                      </h3>
                      <p className="text-xs text-slate-500 capitalize" data-id="element-270">
                        {session.type}
                      </p>
                    </div>
                  </div>
                </div>)}

              <button className="ml-24 w-[calc(100%-6rem)] py-4 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 hover:text-slate-600 hover:border-slate-300 hover:bg-slate-50 transition-all flex items-center justify-center gap-2 text-sm font-medium" data-id="element-271">
                <PlusIcon className="w-4 h-4" data-id="element-272" />
                Add Session
              </button>
            </div>
          </div>

          {/* Right Rail: Library */}
          <div className="w-80 shrink-0 flex flex-col gap-6 overflow-y-auto" data-id="element-273">
            {/* AI Suggestions */}
            <div className="bg-gradient-to-br from-[#0B1B3B] to-slate-900 rounded-2xl p-5 text-white shadow-sm" data-id="element-274">
              <div className="flex items-center gap-2 mb-3" data-id="element-275">
                <SparklesIcon className="w-4 h-4 text-amber-500" data-id="element-276" />
                <h3 className="font-bold text-sm" data-id="element-277">AI Suggestions</h3>
              </div>
              <p className="text-xs text-slate-300 mb-4 leading-relaxed" data-id="element-278">
                Based on your tension around "Resource Allocation", we recommend
                adding a RACI Framework Exercise to Day 2.
              </p>
              <button className="w-full py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors" data-id="element-279">
                Add to Agenda
              </button>
            </div>

            {/* Session Library */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex-1" data-id="element-280">
              <h3 className="font-bold text-slate-900 text-sm mb-4" data-id="element-281">
                Session Library
              </h3>
              <div className="space-y-3" data-id="element-282">
                {library.map((item, i) => <div key={i} className="p-3 border border-slate-200 rounded-lg cursor-grab hover:border-slate-300 hover:shadow-sm transition-all bg-white" data-id="element-283">
                    <h4 className="text-sm font-medium text-slate-900 mb-1" data-id="element-284">
                      {item.title}
                    </h4>
                    <div className="flex items-center gap-3 text-xs text-slate-500" data-id="element-285">
                      <span className="flex items-center gap-1" data-id="element-286">
                        <ClockIcon className="w-3 h-3" data-id="element-287" /> {item.duration}
                      </span>
                      <span className="capitalize px-2 py-0.5 bg-slate-100 rounded-full" data-id="element-288">
                        {item.type}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ClientPortalLayout>;
}